import { useEffect, useRef, useState } from 'react';
import { ForethoughtChat } from '@design-system/ForethoughtChat';
import { PeacockChat } from '@design-system/PeacockChat';
import { useUserStore } from '@hooks/useUserStore';
import environment from '@config/environment';
import TokenManager from '@viome/viome-base/src/api/TokenManager';

interface ChatWidgetProps {
  apiKey: string;
  workflowTag: string;
}

type AclState = 'pending' | 'peacock' | 'forethought';

const ACL_URL = `${environment.PEACOCK_API_BASE}/api.php?fctn=acl-my-status`;
const ACL_TIMEOUT_MS = 10000;

export const ChatWidget = ({ apiKey, workflowTag }: ChatWidgetProps) => {
  const { user } = useUserStore();
  const email = user?.email;
  const [state, setState] = useState<AclState>('pending');
  // StrictMode (and HMR) double-fire effects in dev. We want exactly one ACL
  // request per email change; tracking via ref keeps the second mount from
  // re-issuing a fetch that would race the first on the same connection pool.
  const lastRunForEmail = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!email) {
      setState('forethought');
      return;
    }
    if (lastRunForEmail.current === email) return;
    lastRunForEmail.current = email;

    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort(),
      ACL_TIMEOUT_MS,
    );

    // TokenManager.getToken() returns the persisted `authenticator=...` cookie
    // value (originally delivered by the auth backend as a response header and
    // stored under `set_cookie` in localStorage). Forwarding it via X-Viome-Auth
    // lets api.php verify identity in environments where the cookie can't reach
    // the API host (localhost dev, capacitor://, cross-subdomain configs).
    const cookieValue = TokenManager.getToken();

    fetch(ACL_URL, {
      credentials: 'include',
      signal: controller.signal,
      headers: cookieValue ? { 'X-Viome-Auth': cookieValue } : undefined,
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((json) =>
        setState(json?.acls?.peacock === true ? 'peacock' : 'forethought'),
      )
      .catch(() => setState('forethought'))
      .finally(() => window.clearTimeout(timeoutId));

    return () => window.clearTimeout(timeoutId);
  }, [email]);

  if (state === 'pending') {
    return null;
  }

  if (state === 'peacock' && email) {
    return <PeacockChat email={email} />;
  }

  return <ForethoughtChat apiKey={apiKey} workflowTag={workflowTag} />;
};
